import { apiService } from './api';
import { AccountBalance, AccountHistory, AccountHistoryFilter, ChangePasswordRequest, Transaction } from '../types';

export interface AutoReplenishSettings {
  enabled: boolean;
  thresholdAmount: number;
  replenishAmount: number;
  paymentSource: 'STRIPE_CARD' | 'STRIPE_ACH';
  stripePaymentMethodId?: string;
  lastReplenished?: string;
}

export interface AddFundsResult {
  success: boolean;
  message: string;
  stripePaymentId?: string;
  isDevMode?: boolean;
  newBalance?: number;
}

export interface CardEntryStatus {
  allowed: boolean;
  reason?: string | null;
  isProvisional: boolean;
  emailVerified: boolean;
  phoneVerified: boolean;
}

export interface SavedPaymentMethod {
  paymentMethodId: string;
  type: 'card' | 'bank_account';
  card?: { brand?: string; last4?: string; expMonth?: number; expYear?: number };
  bankAccount?: { bankName?: string; last4?: string; accountType?: string };
}

// Promo transaction type for promotional credit history
export interface PromoTransaction {
  transactionId: string;
  promoCreditId: string;
  sponsorUsername: string;
  campaignName: string;
  transactionType: string;
  amount: number;
  balanceBefore: number;
  balanceAfter: number;
  referenceId?: string;
  referenceType?: string;
  description?: string;
  transactionDate: string;
  splitGroupId?: string;
  vendorCreditsUsed?: number;
  regularCreditsUsed?: number;
  linkedRegularTransactionId?: string;
  linkedPromoTransactionId?: string;
  splitSource?: string;
}

class AccountService {
  /**
   * Get the current user's account balance
   */
  async getBalance(): Promise<AccountBalance> {
    return apiService.get<AccountBalance>('/api/Account/balance');
  }

  /**
   * Get paginated account transaction history
   */
  async getHistory(filter?: AccountHistoryFilter): Promise<AccountHistory> {
    const params = new URLSearchParams();
    
    if (filter?.year) params.append('year', filter.year.toString());
    if (filter?.month) params.append('month', filter.month.toString());
    if (filter?.startDate) params.append('startDate', filter.startDate);
    if (filter?.endDate) params.append('endDate', filter.endDate);
    if (filter?.page) params.append('page', filter.page.toString());
    if (filter?.pageSize) params.append('pageSize', filter.pageSize.toString());
    if (filter?.filterByOtherParty) params.append('filterByOtherParty', filter.filterByOtherParty);
    if (filter?.filterByMyAlias) params.append('filterByMyAlias', filter.filterByMyAlias);
    
    const queryString = params.toString();
    const url = `/api/Account/history${queryString ? `?${queryString}` : ''}`;
    
    return apiService.get<AccountHistory>(url);
  }

  /**
   * Get list of user's aliases for filter dropdown
   */
  async getAliases(): Promise<string[]> {
    return apiService.get<string[]>('/api/Account/aliases');
  }

  /**
   * Change the user's password
   */
  async getPasswordStatus(): Promise<{ userChosenPassword: boolean }> {
    return apiService.get<{ userChosenPassword: boolean }>('/api/Account/password-status');
  }

  async changePassword(request: ChangePasswordRequest): Promise<{ message: string }> {
    return apiService.post<{ message: string }>('/api/Account/change-password', request);
  }

  /**
   * Get transactions for a specific message (by mailId)
   * Returns transaction history entries for each recipient
   */
  async getTransactionsByMailId(mailId: string): Promise<Transaction[]> {
    return apiService.get<Transaction[]>(`/api/Account/transactions/${mailId}`);
  }

  /**
   * Get promotional credit transaction history
   * Shows grants, additions, usage, and refunds
   */
  async getPromoHistory(limit: number = 50, sponsorId?: string): Promise<PromoTransaction[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (sponsorId) {
      params.append('sponsorId', sponsorId);
    }
    return apiService.get<PromoTransaction[]>(`/api/Account/promo-history?${params.toString()}`);
  }
  
  /**
   * Add funds to the mailbox account via Stripe card or ACH.
   */
  async addFunds(amount: number, paymentMethodId?: string, paymentType?: string, devMode?: boolean): Promise<AddFundsResult> {
    return apiService.post<AddFundsResult>('/api/Account/add-funds', {
      amount,
      paymentMethodId,
      paymentType,
      devMode,
    });
  }

  /**
   * Whether this account may enter payment details yet. Provisional accounts must verify
   * an email address or a phone number before card entry is allowed.
   */
  async getCardEntryStatus(): Promise<CardEntryStatus> {
    return apiService.get<CardEntryStatus>('/api/Account/card-entry-status');
  }

  /**
   * Get auto-replenish settings for the current user.
   */
  async getAutoReplenish(): Promise<AutoReplenishSettings> {
    return apiService.get<AutoReplenishSettings>('/api/Account/auto-replenish');
  }

  /**
   * Save auto-replenish settings for the current user.
   */
  async saveAutoReplenish(settings: AutoReplenishSettings): Promise<{ success: boolean; message: string }> {
    return apiService.post('/api/Account/auto-replenish', {
      enabled: settings.enabled,
      thresholdAmount: settings.thresholdAmount,
      replenishAmount: settings.replenishAmount,
      paymentSource: settings.paymentSource,
      stripePaymentMethodId: settings.stripePaymentMethodId,
    });
  }

  async listPaymentMethods(): Promise<SavedPaymentMethod[]> {
    const response = await apiService.get<{ success: boolean; paymentMethods: SavedPaymentMethod[] }>(
      '/api/payment-methods/list',
    );
    return response.paymentMethods || [];
  }

  async setupCardPaymentMethod(): Promise<{ clientSecret: string; setupIntentId?: string }> {
    return apiService.post('/api/payment-methods/setup/card', {});
  }

  async setupBankPaymentMethod(): Promise<{ clientSecret: string; setupIntentId?: string }> {
    return apiService.post('/api/payment-methods/setup/bank-account', {});
  }

  // ── Account Closure ──────────────────────────────────────────────────────

  /**
   * Step 1: Request a 6-digit account-closure verification code.
   * Returns the masked email address the code was sent to.
   */
  async requestAccountClosure(): Promise<{ success: boolean; maskedEmail?: string; message?: string }> {
    return apiService.post('/api/Account/request-closure', {});
  }

  /**
   * Step 2: Confirm account closure with the 6-digit code.
   * On success the client must immediately log the user out.
   */
  async confirmAccountClosure(code: string): Promise<{ success: boolean; message?: string }> {
    return apiService.post('/api/Account/confirm-closure', { code });
  }

  /**
   * Export account history as CSV file download
   */
  async exportHistory(filter?: AccountHistoryFilter): Promise<void> {
    const params = new URLSearchParams();
    
    if (filter?.year) params.append('year', filter.year.toString());
    if (filter?.month) params.append('month', filter.month.toString());
    if (filter?.startDate) params.append('startDate', filter.startDate);
    if (filter?.endDate) params.append('endDate', filter.endDate);
    if (filter?.filterByOtherParty) params.append('filterByOtherParty', filter.filterByOtherParty);
    if (filter?.filterByMyAlias) params.append('filterByMyAlias', filter.filterByMyAlias);
    
    const queryString = params.toString();
    const baseUrl = apiService.getBaseUrl();
    const url = `${baseUrl}/api/Account/history/export${queryString ? `?${queryString}` : ''}`;
    
    // Use fetch with authorization header for blob download
    const token = apiService.getAccessToken();
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error('Failed to export account history');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `account-history-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

export const accountService = new AccountService();
export default accountService;

